// src/pages/notifications/notification-list/index.jsx
import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Col, Row, Card, CardBody, Input, Button } from "reactstrap";
import Breadcrumbs from "../../../components/Common/Breadcrumb";
import TableContainer from "../../../components/Common/TableContainer";
import Paginations from "../../../components/Common/Pagination";
import Spinners from "../../../components/Common/Spinner";
import { useGetAllNotifications, useMarkNotificationAsSeen } from "../../../api/notifications";
import { getNotificationRedirectPath } from "../../../utils/notificationRedirect";
import { Badge } from "reactstrap";
import { useSelector } from "react-redux";
import { selectRole } from "../../../store/auth/selectors";

const NotificationList = () => {
  document.title = "Notifications List | Admin";
  const navigate = useNavigate();
  const role = useSelector(selectRole);
  const notificationsEndpoint =
    role === "worker" ? "notifications/all" : "admin/notifications/all";

  // server-side controls
  const [page, setPage] = useState(1); // 1-based
  const [limit, setLimit] = useState(10);
  const [search, setSearch] = useState("");
  const [searchInput, setSearchInput] = useState("");

  // mutation hook (GET)
  const { mutate, data, isLoading, isPending, error } = useGetAllNotifications({
    basePath: notificationsEndpoint
  });
  const { mutate: markNotificationAsSeen } = useMarkNotificationAsSeen();
  const loading = isLoading || isPending;

  // Build a stable fetch key and guard against duplicate calls
  const lastFetchKeyRef = useRef("");
  const fetchKey = JSON.stringify({
    page,
    limit,
    search
  });

  // fire fetch whenever fetchKey changes
  useEffect(() => {
    if (lastFetchKeyRef.current === fetchKey) return;
    lastFetchKeyRef.current = fetchKey;
    mutate({
      page,
      limit,
      search: search.trim() || undefined
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fetchKey]);

  const payload = data?.data ?? data;
  const notifications = payload?.notifications ?? [];
  const total = payload?.total ?? 0;

  // Format time ago
  const getTimeAgo = (date) => {
    if (!date) return "-";
    const now = new Date();
    const diff = Math.floor((now - new Date(date)) / 1000);

    if (diff < 60) return `${diff} sec ago`;
    if (diff < 3600) return `${Math.floor(diff / 60)} min ago`;
    if (diff < 86400) return `${Math.floor(diff / 3600)} hours ago`;
    return `${Math.floor(diff / 86400)} days ago`;
  };

  const columns = useMemo(
    () => [
      {
        header: "Title",
        accessorKey: "title",
        enableColumnFilter: false,
        cell: (info) => {
          const notification = info.row.original;
          return (
            <div>
              <div className="fw-medium">{notification.title || "-"}</div>
              {!notification.seen && <Badge className="bg-danger ms-2">New</Badge>}
            </div>
          );
        }
      },
      {
        header: "Message",
        accessorKey: "message",
        enableColumnFilter: false,
        cell: (info) => {
          const message = info.getValue();
          return (
            <div className="text-muted" style={{ maxWidth: "400px" }}>
              {message || "-"}
            </div>
          );
        }
      },
      {
        header: "Sender",
        accessorKey: "senderMode",
        enableColumnFilter: false,
        cell: (info) => {
          const notification = info.row.original;
          const senderMode = notification.senderMode;
          if (senderMode) {
            return <Badge className="bg-info">{senderMode}</Badge>;
          }
          return "-";
        }
      },
      {
        header: "Status",
        accessorKey: "seen",
        enableColumnFilter: false,
        cell: (info) => {
          const seen = info.getValue();
          return seen ? (
            <Badge className="bg-secondary">Read</Badge>
          ) : (
            <Badge className="bg-warning text-dark">Unread</Badge>
          );
        }
      },
      {
        header: "Created At",
        accessorKey: "createdAt",
        enableColumnFilter: false,
        cell: (info) => {
          const date = info.getValue();
          if (!date) return "-";
          return (
            <div>
              <div>{new Date(date).toLocaleDateString()}</div>
              <small className="text-muted">{getTimeAgo(date)}</small>
            </div>
          );
        }
      }
    ],
    []
  );

  return (
    <div className="page-content">
      <div className="container-fluid">
        <Breadcrumbs title="Notifications" breadcrumbItem="Notifications List" />

        <Row className="mb-3 g-2 align-items-center">
          {/* Search */}
          <Col md={6}>
            <Input
              type="text"
              placeholder="Search by title, message..."
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  setPage(1);
                  setSearch(searchInput.trim());
                }
              }}
            />
          </Col>
        </Row>

        <Row className="mb-3 g-2 align-items-center">
          <Col md="auto" className="ms-auto d-flex gap-2 align-items-center">
            <Button
              color="primary"
              onClick={() => {
                setPage(1);
                setSearch(searchInput.trim());
              }}
              disabled={loading}
            >
              <i className="mdi mdi-magnify me-1" />
              Search
            </Button>
            <Button
              color="secondary"
              outline
              onClick={() => {
                setSearch("");
                setSearchInput("");
                setLimit(10);
                setPage(1);
              }}
              disabled={loading}
            >
              Reset
            </Button>
            {/* Page size */}
            <Input
              type="select"
              value={limit}
              onChange={(e) => {
                setLimit(Number(e.target.value));
                setPage(1);
              }}
              style={{ width: "auto", minWidth: "100px" }}
            >
              {[10, 20].map((n) => (
                <option key={n} value={n}>
                  Show {n}
                </option>
              ))}
            </Input>
            <Button
              color="light"
              onClick={() =>
                mutate({
                  page,
                  limit,
                  search: search.trim() || undefined
                })
              }
              disabled={loading}
            >
              <i className="mdi mdi-refresh" />
            </Button>
          </Col>
        </Row>

        <Card>
          <CardBody>
            {loading && notifications.length === 0 ? (
              <Spinners setLoading={() => {}} />
            ) : error ? (
              <div className="text-danger">Failed to load notifications.</div>
            ) : (
              <>
                <TableContainer
                  columns={columns}
                  data={notifications}
                  isPagination={false}
                  isCustomPageSize={false}
                  isGlobalFilter={false}
                  isJobListGlobalFilter={false}
                  tableClass="align-middle table-nowrap dt-responsive nowrap w-100 table-check dataTable no-footer dtr-inline mt-2 border-top"
                  theadClass="table-light"
                  divClassName="table-responsive"
                  onRowClick={(row) => {
                    const notif = row.original;
                    
                    // Mark notification as seen when clicked
                    if (!notif.seen && notif.id) {
                      markNotificationAsSeen(notif.id);
                    }
                    
                    // Determine redirect slug and reference based on notification type
                    let redirectSlug = notif.redirectSlug;
                    let reference = notif.reference;

                    // If bookingRequestId exists, it's a booking request notification
                    // Use the booking slug from invite.booking.slug for redirect
                    if (notif.bookingRequestId) {
                      if (notif.invite?.booking?.slug) {
                        redirectSlug = notif.invite.booking.slug;
                        reference = "booking";
                      } else if (notif.invite?.slug) {
                        // Fallback to booking-request slug if booking slug not available
                        redirectSlug = notif.invite.slug;
                        reference = "booking-request";
                      }
                    }
                    // If booking exists but no bookingRequestId, it's a booking notification
                    else if (notif.invite?.booking?.slug) {
                      redirectSlug = notif.invite.booking.slug;
                      reference = "booking";
                    }
                    // If userSlug exists, use it
                    else if (notif.userSlug) {
                        redirectSlug = notif.userSlug;
                        reference = notif.reference || notif.senderMode || notif.sender?.role;
                    }

                    // Navigate using reference and redirectSlug
                    const redirectPath = getNotificationRedirectPath(
                      reference,
                      redirectSlug,
                      notif.senderMode
                    );
                    if (redirectPath) {
                      navigate(redirectPath);
                    } else if (notif.link) {
                      navigate(notif.link);
                    }
                  }}
                />

                <Paginations
                  total={total}
                  perPageData={limit}
                  currentPage={page}
                  setCurrentPage={setPage}
                  isShowingPageLength
                  paginationDiv="dataTables_paginate paging_simple_numbers pagination-rounded d-flex justify-content-end mt-3"
                  paginationClass="pagination"
                />
              </>
            )}
          </CardBody>
        </Card>
      </div>
    </div>
  );
};

export default NotificationList;
